import { useTranslation } from 'react-i18next';
import { useMemo, useEffect } from 'react';
import { FormEvent, useState } from 'react';
import { useSelector } from 'react-redux';
import { getInquiryTypes } from './utils'; // 250415 언어전환목적 변경

import { Button, Checkbox, DescriptionBar, InfoMessage, Input, InputTextArea, Label, SelectBox, Text } from 'components';


import contactImage from 'assets/contactImage.jpeg';
import CountrySelector from './CountrySelector'; 
import { getCountryCode, getCountryData } from 'countries-list';
import axios from 'axios';
import axiosAuth from 'common/AxiosAuth'; // 로그인한 유저용 axios
import RegisterModal from './RegisterModal';
import { Message } from 'types/message';
import { InformationCircleOutlineIcon } from 'assets/svgs';
import 'styles/mobile.css';
import { Helmet } from 'react-helmet-async';


const ContactUs = () => {

  const { t } = useTranslation();
  
  // Redux store에서 로그인 상태 확인
  const userInfo = useSelector((state: any) => state.loginSlice?.userInfo);
  const isLoggedIn = !!userInfo; // userInfo가 존재하면 로그인된 상태
  
  const inquiryTypes = useMemo(() => getInquiryTypes(t), [t]); // 250415 드롭박스 즉각 언어변경을위함
  const [inquiryType, setInquiryType] = useState<number>(inquiryTypes[0].value);

  const [messageArray, setMessageArray] = useState<Message[]>([]);
  const [messageArrayCount, setMessageArrayCount] = useState(0);
  const [name, setName] = useState<string>('');
  const [company, setCompany] = useState<string>('');
  const [country, setCountry] = useState<any>(null);  // 타입을 any로 설정
  const [contact, setContact] = useState<string>('');
  const [inquiry, setInquiry] = useState<string>('');
  const [isAgree, setIsAgree] = useState<boolean>(false);
  const [isRegisterModalOpened, setIsRegisterModalOpened] = useState<boolean>(false);
  const [modalMessage, setModalMessage] = useState<string>('');

  function onClickDeleteButton(index: number) {
    setMessageArray((prev) => prev.filter((message: any) => message.index !== index));
  }

  // 로그인 사용자의 이름 자동 입력
  useEffect(() => {
    if (isLoggedIn && userInfo?.name && !name) {
      setName(userInfo.name);
    }
  }, [isLoggedIn, userInfo?.name, name]);

  // 로그인 사용자의 이메일 자동 입력
  useEffect(() => {
    if (isLoggedIn && userInfo?.email && !contact) {
      setContact(userInfo.email);
    }
  }, [isLoggedIn, userInfo?.email, contact]);

  // 로그인 사용자 회사명 자동 입력 (회원 정보 조회)
  useEffect(() => {
    const fillCompanyFromProfile = async () => {
      try {
        if (!isLoggedIn) return;
        const needCompany = !company;
        const needCountry = !country;
        if (!needCompany && !needCountry) return;
        const res = await axiosAuth.get('/api/member/myPage');
        const data = res.data;
        if (needCompany && data?.company) {
          setCompany(data.company);
        }
        if (needCountry && data?.country) {
          // CountrySelector는 {value,label} 구조를 사용
          setCountry({ value: data.country, label: data.country });
        }
      } catch (e) {
        console.error('Failed to fetch member profile for company:', e);
      }
    };
    fillCompanyFromProfile();
  }, [isLoggedIn, company, country]);


  function onSubmit(e: FormEvent) {
    e.preventDefault();

    const countryCode = country ? getCountryCode(country.label) : '';
    const countryData = countryCode ? getCountryData(countryCode) : null;
    
  }

  function onCheck(value: boolean) {
    setIsAgree(value);
  }
  
  const emailRegexp = new RegExp(/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/g);

  async function onSave () {
    if (!name) {
      setIsRegisterModalOpened(true);
      setModalMessage('Please fill in Name');
      return;
    }else if(!contact){
      setIsRegisterModalOpened(true);
      setModalMessage('Please fill in Email.');
      return;
    }else if(!emailRegexp.test(contact)) {
      setIsRegisterModalOpened(true);
      setModalMessage('Invalid email address.');
      return;
    }else if(!isAgree){
      setIsRegisterModalOpened(true);
      setModalMessage('Please agree to the privacy policy and terms of use.');
      return;
    }

    const params = {
      writerEmail : contact,
      writerName : name,
      inquiryType : inquiryType,
      inquiryContents :inquiry ,
      organization: company,
      country: country?.label || 'Unknown',
      isAgreePrivacy: isAgree,
    };
    
    // 로그인 상태에 따라 다른 axios 인스턴스 사용
    const apiClient = isLoggedIn ? axiosAuth : axios;
    console.log('API 호출:', isLoggedIn ? 'AxiosAuth (회원)' : 'axios (비회원)');
    
    const res = await apiClient.post('/api/contactUs/register', params);
    console.log('resres',res);
    // Form reset
    setInquiryType(inquiryTypes[0].value);
    setName('');
    setCompany('');
    setCountry(null);
    setContact('');
    setInquiry('');
    setIsAgree(false);

    setMessageArray((prev) => [
      ...prev,
      {
        index: messageArrayCount,
        icon: <InformationCircleOutlineIcon />,
        text: `Registration has been completed `
      }
    ]);
    setTimeout(() => onClickDeleteButton(messageArrayCount), 3000);
    setMessageArrayCount((prev) => prev + 1);

  }

    // RegisterModal의 onConfirm 핸들러
    function handleModalConfirm() {
      setIsAgree(true); // 체크박스를 체크 상태로 업데이트
      setIsRegisterModalOpened(false);
  }

  return (
    <>
      <Helmet>
        <title>Contact Us | IOPS SPACE</title>
        <meta name="description" content="Do you need advice on using IOPS Ground Station?" />
      </Helmet>
      <div className="flex w-full flex-col items-center">
        <DescriptionBar
          className="mb-4 w-full max-w-[800px] md:mb-2"
          title={t('contactUs.pageTitle')}
  description={t('contactUs.pageDescription')}
        />
        <div className="relative mb-2 flex h-[120px] w-full items-center justify-center ">
          <img className="h-full w-full brightness-50	" src={contactImage} alt="contact background" />
          <Text
            className="absolute top-0 flex h-full w-full max-w-[800px] items-center justify-start px-4"
            size="body1"
            weight="regular"
            color="white"
          >
            {t('contactUs.descriptionBanner')}
          </Text>
        </div>
        <form className="flex w-full max-w-[800px] flex-col gap-6 px-4 py-6 padding-md" onSubmit={onSubmit}>
          <SelectBox
            className="w-full max-w-none"
            bgColor="layer2"
            list={inquiryTypes}
            label={t('contactUs.form.inquiryType')}
            value={inquiryType}
            setValue={setInquiryType}
          />
          <Input label={t('contactUs.form.name')} value={name} setValue={setName}  />
          <Input label={t('contactUs.form.company')} value={company} setValue={setCompany} />
          <div className="flex flex-row items-end gap-4 display-md">
            <div className="md:w-[280px]">
              <Label
                  label={t('contactUs.form.country')}
                  htmlFor={'Country'}
                />
              <CountrySelector  value={country} onChange={setCountry} /> 
            </div>
            <Input className="flex-1 pt-md" label={t('contactUs.form.email')} value={contact} setValue={setContact} />
          </div>
          <InputTextArea inputClassName="h-[200px]" label={t('contactUs.form.inquiry')} value={inquiry} setValue={setInquiry} />
          <div className="flex w-full flex-row items-center justify-between">
            <div className="flex flex-row items-center gap-2">
              <Checkbox value={isAgree} onChange={onCheck} inputKey="agree" />
              <Text size="body2" weight="regular" color="secondary">
              {t('contactUs.form.agree')}
              </Text>
            </div>
            <Button className="w-[75px]" htmlType="submit" type="blue" text={t('contactUs.form.submit')} size="medium" onClick={onSave} aria-label="question submit"/>
          </div>
        </form>
        <RegisterModal isOpened={isRegisterModalOpened} setIsOpened={setIsRegisterModalOpened} onConfirm={handleModalConfirm} message={modalMessage}/>
        <InfoMessage messageArray={messageArray} onClickDelete={onClickDeleteButton} />
      </div>
    </>
  );
};

export default ContactUs;
